import { dayjs } from '#gp/app-common/util/dayjs';
import { gpCommon } from '../app';
import { AuthCheckResultChoices, AuthorisationCheckerService, parseJwtToken } from './authorisation-checker-service';
import { ILogglyService } from './jsloggly-service';
import { IStorageService } from './local-storage-service';


interface AuthData {
    email: string;
    token: string;
    userId: string;
}

export interface IAuthorisationStatus {
    loginChecking: boolean;
    email: string;
    userId: string;
    token: string;
    loggedInStatus: AuthCheckResultChoices;
    isAuthorised: boolean;
}

export interface IAuthorisationCheckerService {
    authorisationChanged(callback: AuthoisationStatusCallback): void;
    checkAuthorisationIfNeeded(): ng.IPromise<IAuthorisationStatus>;
    resetCheckedStatus(): void;
}

type AuthoisationStatusCallback = (authStatus: IAuthorisationStatus) => void

class AuthorisationCheckerWithCachingService implements IAuthorisationCheckerService {
    private DEBUG_DELAY = 0;
    private listeners: ((authStatus: IAuthorisationStatus) => void)[] = [];
    private checkAuthPromise: ng.IPromise<IAuthorisationStatus> | null = null;
    private lastAuthorisationStatus: IAuthorisationStatus = {
        email: "",
        token: "",
        userId: "",
        loginChecking: false,
        loggedInStatus: 'unauthorized',
        isAuthorised: false,
    };

    public static readonly serviceName = "gpAuthCheckerServiceOld"
    static $inject = ['$http',
        '$q',
        'gpLocalStorageService',
        '$timeout',
        'gpLogglyService',
        AuthorisationCheckerService.serviceName,];

    constructor(public $http: ng.IHttpService,
        public $q: ng.IQService,
        public storage: IStorageService,
        public $timeout: ng.ITimeoutService,
        public logService: ILogglyService,
        private authorisationCheckerService: AuthorisationCheckerService,
    ) {
        this.resetCheckedStatus();
    }

    public authorisationChanged(callback: AuthoisationStatusCallback) {
        this.listeners.push(callback);
        callback(this.lastAuthorisationStatus);
    }

    private onAuthorisationChanged(authStatus: IAuthorisationStatus) {
        this.listeners.forEach(l => l(authStatus));
    }

    public resetCheckedStatus() {
        this.checkAuthPromise = null;
        let authData = this.loadAuthorisationData();

        this.lastAuthorisationStatus = {
            email: authData.email,
            token: authData.token,
            userId: authData.userId,
            loginChecking: false,
            loggedInStatus: 'notsure',
            isAuthorised: !!authData.userId,
        };

        this.onAuthorisationChanged(this.lastAuthorisationStatus);
    }

    public getAuthorisationStatus(): IAuthorisationStatus {
        return this.lastAuthorisationStatus;
    }

    private loadAuthorisationData(): AuthData {

        let authData = this.storage.getAsObject<{ email: string, token: string }>('authorizationData');
        if (authData) {
            const jwt = parseJwtToken(authData.token);
            return {
                email: authData.email,
                token: authData.token,
                userId: (jwt ? jwt.payload.nameid : "") || "",
            }
        } else {
            return {
                email: "",
                token: "",
                userId: "",
            }
        }
    }

    private checkAuthorisationNowWithoutNotify(): ng.IPromise<IAuthorisationStatus> {
        let authData = this.loadAuthorisationData();
        this.lastAuthorisationStatus.loginChecking = true;

        var checkPromise = this.authorisationCheckerService.checkAuthorisation(authData)
            .then(status => {
                var result: IAuthorisationStatus = {
                    email: status.email,
                    token: status.token,
                    userId: status.userId,
                    loggedInStatus: status.userId ? status.loggedInStatus : 'unauthorized',
                    loginChecking: false,
                    isAuthorised: !!status.userId && status.loggedInStatus !== 'unauthorized',

                };
                setGameplanTokenCookie(status.token);
                this.lastAuthorisationStatus = result;
                return result;
            })
            .catch(reason => {
                throw reason;
            })

        return checkPromise;
    }

    public checkAuthorisationIfNeeded(): ng.IPromise<IAuthorisationStatus> {
        if (!this.checkAuthPromise) {

            let delayed = this.$q.defer<IAuthorisationStatus>();

            let authChecker = this.checkAuthorisationNowWithoutNotify();

            let newCheckAuthPromise: ng.IPromise<IAuthorisationStatus>;

            if (this.DEBUG_DELAY > 0) {
                authChecker.then(status => this.$timeout(() => delayed.resolve(status), 2000)).catch(err => { });
                authChecker.catch(reason => this.$timeout(() => delayed.reject(reason), 2000));

                newCheckAuthPromise = delayed.promise;
            } else {
                newCheckAuthPromise = authChecker;
            }

            newCheckAuthPromise.then(authStatus => {
                this.onAuthorisationChanged(authStatus);
            }).catch(err => { });

            newCheckAuthPromise.catch(reason => {
            });

            this.checkAuthPromise = newCheckAuthPromise;

        }

        return this.checkAuthPromise;
    }

    private lastAuthLogTime: dayjs.Dayjs;

}

export const IAuthorisationCheckerService_serviceName = AuthorisationCheckerWithCachingService.serviceName;
gpCommon.service(AuthorisationCheckerWithCachingService.serviceName, AuthorisationCheckerWithCachingService);

export function setGameplanTokenCookie(token: string) {

    document.cookie = `latestgameplantoken=${token}`;

}

